import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import FormInput from '../components/FormInput'
import { authApi } from '../api/auth'
import { toastUtil } from '../utils/toast'
import { validatePassword } from '../utils/passwordValidation'
import { verifyPhoneForPasswordFind } from '../utils/phoneVerification'

const PasswordFind: React.FC = () => {
  const navigate = useNavigate()
  const [phone, setPhone] = useState('')
  const [isVerified, setIsVerified] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [isResetting, setIsResetting] = useState(false)

  const validatePasswordForm = (): boolean => {
    let isValid = true

    const passwordErrorMsg = validatePassword(newPassword)
    if (passwordErrorMsg) {
      setNewPasswordError(
        passwordErrorMsg === '비밀번호를 입력해주세요'
          ? '새로운 비밀번호를 입력해주세요'
          : passwordErrorMsg
      )
      isValid = false
    } else {
      setNewPasswordError('')
    }

    if (!confirmPassword) {
      setConfirmPasswordError('새로운 비밀번호를 다시 입력해주세요')
      isValid = false
    } else if (newPassword && confirmPassword !== newPassword) {
      setConfirmPasswordError('새로운 비밀번호가 일치하지 않습니다')
      isValid = false
    } else {
      setConfirmPasswordError('')
    }

    return isValid
  }

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewPassword(value)
    if (newPasswordError) {
      setNewPasswordError('')
    }
  }

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (newPassword && value !== newPassword) {
      setConfirmPasswordError('새로운 비밀번호가 일치하지 않습니다')
    } else {
      setConfirmPasswordError('')
    }
  }

  const handleVerificationClick = async () => {
    setIsVerifying(true)
    try {
      const verifiedPhoneNumber = await verifyPhoneForPasswordFind()
      if (verifiedPhoneNumber) {
        setPhone(verifiedPhoneNumber)
        setIsVerified(true)
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '본인인증 처리 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsVerifying(false)
    }
  }

  const handlePasswordReset = async () => {
    if (!validatePasswordForm()) {
      return
    }

    setIsResetting(true)
    try {
      await authApi.resetPassword({
        phone: phone,
        new_password: newPassword,
      })
      toastUtil.success('비밀번호가 재설정되었습니다')
      navigate('/login')
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '비밀번호 재설정 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsResetting(false)
    }
  }

  const isResetButtonDisabled =
    !newPassword ||
    !confirmPassword ||
    !!newPasswordError ||
    !!confirmPasswordError ||
    isResetting

  const renderVerificationBody = () => {
    return (
      <div className="flex flex-col">
        <h1 className="text-style-labelL text-textColorText2 mb-8px">
          본인인증
        </h1>
        <p className="text-style-bodyM text-textColorText2 mb-10px">
          * 비밀번호를 재설정하려면 본인인증이 필요합니다
        </p>

        <button
          type="button"
          onClick={handleVerificationClick}
          disabled={isVerifying}
          className={`
            w-full
            py-15px
            rounded-30px
            font-bold
            text-16px
            ${
              isVerifying
                ? 'cursor-not-allowed bg-brandPrimary-10% text-textColorText3'
                : 'cursor-pointer bg-4B3621-80% text-textColorText1W'
            }
          `}
        >
          {isVerifying ? '본인인증 중...' : '본인인증'}
        </button>
      </div>
    )
  }

  const renderPasswordResetBody = () => {
    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <FormInput
          label="새로운 비밀번호"
          name="newPassword"
          type="password"
          value={newPassword}
          onChange={handleNewPasswordChange}
          placeholder="새로운 비밀번호를 입력해주세요"
          showPasswordToggle
          error={newPasswordError}
          marginBottom={16}
        />

        <FormInput
          label="새로운 비밀번호 재입력"
          name="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          placeholder="새로운 비밀번호를 다시 입력해주세요"
          showPasswordToggle
          error={confirmPasswordError}
          marginBottom={32}
        />

        <button
          type="button"
          onClick={handlePasswordReset}
          disabled={isResetButtonDisabled}
          className={`
            w-full
            py-15px
            rounded-30px
            font-bold
            font-16px
            ${
              isResetButtonDisabled
                ? 'cursor-not-allowed bg-brandPrimary-10% text-textColorText3'
                : 'cursor-pointer bg-4B3621-80% text-textColorText1W'
            }
          `}
        >
          {isResetting ? '재설정 중...' : '비밀번호 재설정하기'}
        </button>
      </form>
    )
  }

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <AppBar title="비밀번호 재설정" isBottomBorder />

        <div className="app-container">
          <div className="flex-1 overflow-y-auto scrollbar-hide p-16px">
            {isVerified ? renderPasswordResetBody() : renderVerificationBody()}
          </div>
        </div>
      </div>

    </>
  )
}

export default PasswordFind
